import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export type ProgressTone = 'accent' | 'success' | 'warning' | 'danger';

export interface ProgressProps {
  value: number;
  max?: number;
  className?: string;
  tone?: ProgressTone;
  size?: 'sm' | 'md';
  /** Unknown duration — animates a travelling bar instead of a fill. */
  indeterminate?: boolean;
  label?: string;
}

const TONES: Record<ProgressTone, string> = {
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

export function Progress({
  value,
  max = 100,
  className,
  tone = 'accent',
  size = 'sm',
  indeterminate,
  label,
}: ProgressProps) {
  const percent = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={indeterminate ? undefined : Math.round(percent)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn(
        'relative w-full overflow-hidden rounded-full bg-sunken',
        size === 'sm' ? 'h-1.5' : 'h-2.5',
        className,
      )}
    >
      {indeterminate ? (
        <div
          className={cn(
            'absolute inset-y-0 w-1/3 rounded-full animate-[var(--animate-progress-slide)]',
            TONES[tone],
          )}
        />
      ) : (
        <div
          className={cn('h-full rounded-full transition-[width] duration-500 ease-out', TONES[tone])}
          style={{ width: `${percent}%` }}
        />
      )}
    </div>
  );
}

export interface ProgressRingProps {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  tone?: ProgressTone;
  className?: string;
  children?: ReactNode;
}

/** Circular variant — step counters and quota dials. */
export function ProgressRing({
  value,
  max = 100,
  size = 40,
  strokeWidth = 4,
  tone = 'accent',
  className,
  children,
}: ProgressRingProps) {
  const percent = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <span
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuenow={Math.round(percent * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-sunken"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - percent)}
          className={cn(
            'transition-[stroke-dashoffset] duration-500 ease-out',
            tone === 'accent' && 'stroke-accent',
            tone === 'success' && 'stroke-success',
            tone === 'warning' && 'stroke-warning',
            tone === 'danger' && 'stroke-danger',
          )}
        />
      </svg>
      {children && (
        <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-fg">
          {children}
        </span>
      )}
    </span>
  );
}
